import { DestroyRef, Directive, effect, ElementRef, inject, input, OnInit, Renderer2, Signal, untracked } from '@angular/core';

/**
 * Directive that scrolls its host element into view in response to
 * configurable DOM events and/or a reactive signal transition.
 *
 * Designed for use on accordion items, edit buttons, form fields, or any
 * element that should become visible when activated.
 *
 * @example Basic usage (scrolls on click and focus by default)
 * ```html
 * <button zagScrollIntoView>Edit</button>
 * ```
 *
 * @example Signal-based trigger (e.g., accordion expand)
 * ```html
 * <section zagScrollIntoView [scrollOnSignal]="expanded" [scrollDelay]="100">
 *   ...
 * </section>
 * ```
 *
 * @example Custom events and scroll options
 * ```html
 * <div
 *   zagScrollIntoView
 *   [scrollEvents]="['mouseenter']"
 *   scrollBehavior="instant"
 *   scrollBlock="center"
 * >
 * ```
 *
 * @example Disabled
 * ```html
 * <button [zagScrollIntoView]="false">No scroll</button>
 * ```
 */
@Directive({
  selector: '[zagScrollIntoView]',
})
export class ScrollIntoViewDirective implements OnInit {
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Master enable/disable toggle.
   * When `false`, all scroll triggers are suppressed.
   * @default true
   */
  readonly zagScrollIntoView = input<boolean>(true);

  /**
   * Scroll animation behaviour.
   * @default 'smooth'
   */
  readonly scrollBehavior = input<ScrollBehavior>('smooth');

  /**
   * Vertical alignment of the element after scrolling.
   * @default 'nearest'
   */
  readonly scrollBlock = input<ScrollLogicalPosition>('nearest');

  /**
   * Horizontal alignment of the element after scrolling.
   * @default 'nearest'
   */
  readonly scrollInline = input<ScrollLogicalPosition>('nearest');

  /**
   * Delay in milliseconds before executing the scroll.
   * Useful for waiting on DOM updates after conditional rendering
   * (e.g., `@if` blocks in accordions).
   * @default 0
   */
  readonly scrollDelay = input(0);

  /**
   * Minimum delay applied on Android devices to wait for virtual keyboard/layout
   * shifts before scrolling.
   * @default 200
   */
  readonly scrollAndroidMinDelay = input(200);

  /**
   * DOM events on the host element that trigger a scroll.
   * @default ['click', 'focus']
   */
  readonly scrollEvents = input<readonly string[]>(['click', 'focus']);

  /**
   * A signal whose `false -> true` transitions trigger a scroll.
   * Designed to work with Angular's `model()` signals
   * (e.g., `AccordionItemComponent.expanded`).
   */
  readonly scrollOnSignal = input<Signal<boolean> | undefined>(undefined);

  private pendingTimeout: ReturnType<typeof setTimeout> | null = null;
  private readonly isAndroidDevice = /Android/i.test(globalThis.navigator?.userAgent ?? '');

  constructor() {
    this.watchSignal();
    this.destroyRef.onDestroy(() => this.clearPendingTimeout());
  }

  ngOnInit(): void {
    this.registerEventListeners();
  }

  private watchSignal(): void {
    let previousValue: boolean | undefined;

    effect(() => {
      const signalFn = this.scrollOnSignal();
      if (!signalFn) {
        previousValue = undefined;
        return;
      }

      const currentValue = signalFn();
      const prev = previousValue;
      previousValue = currentValue;

      // Only scroll on false -> true transitions (not on initial value)
      if (prev === false && currentValue === true) {
        untracked(() => this.scheduleScroll());
      }
    });
  }

  private registerEventListeners(): void {
    const events = this.scrollEvents();
    const unlisten: Array<() => void> = [];

    for (const eventName of events) {
      const dispose = this.renderer.listen(this.elementRef.nativeElement, eventName, () => {
        this.scheduleScroll();
      });
      unlisten.push(dispose);
    }

    this.destroyRef.onDestroy(() => {
      for (const dispose of unlisten) {
        dispose();
      }
    });
  }

  private scheduleScroll(): void {
    if (!this.zagScrollIntoView()) {
      return;
    }

    this.clearPendingTimeout();

    const delay = this.getEffectiveDelay();
    if (delay > 0) {
      this.pendingTimeout = setTimeout(() => {
        this.pendingTimeout = null;
        this.executeScroll();
      }, delay);
    } else {
      this.executeScroll();
    }
  }

  private getEffectiveDelay(): number {
    const configuredDelay = this.scrollDelay();

    if (!this.isAndroidDevice) {
      return configuredDelay;
    }

    return Math.max(configuredDelay, this.scrollAndroidMinDelay());
  }

  private executeScroll(): void {
    this.elementRef.nativeElement.scrollIntoView({
      behavior: this.scrollBehavior(),
      block: this.scrollBlock(),
      inline: this.scrollInline(),
    });
  }

  private clearPendingTimeout(): void {
    if (this.pendingTimeout !== null) {
      clearTimeout(this.pendingTimeout);
      this.pendingTimeout = null;
    }
  }
}
